import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import PrintIcon from '@mui/icons-material/Print';
import { theme } from '../../theme/theme';
import {
  Payment,
  PropertyInvoiceWithRelations,
  useGetPropertyByIdQuery,
  useGetPaymentsUserQuery,
} from '../../redux/residentPortalApi';
import { formatCurrency } from '../../utils/currencyUtil';
import ResponsiveRecordCard from '../common/ResponsiveRecordCard';

type StatementRow = {
  id: string;
  date: Date | null;
  dateLabel: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  kind: 'debit' | 'credit';
  status: string;
  outstandingAmount: number;
  excludeFromCalculations: boolean;
};

interface OwnProps {
  propertyId: string;
  invoices?: PropertyInvoiceWithRelations[];
}

const isSucceededPadelPayment = (payment: Payment) => {
  const purpose = (payment.purpose || '').toLowerCase();
  const status = (payment.status || '').toLowerCase();
  const succeededStatuses = ['succeeded', 'success', 'successful', 'paid', 'completed', 'complete'];
  const isPadelPurpose =
    purpose === 'pedalcourtinvoicebalance' ||
    purpose === 'pedal court invoice balance' ||
    purpose === 'pedal court invoice' ||
    purpose === 'pedal court balance' ||
    purpose === 'padel court invoice balance' ||
    purpose === 'padel court invoice' ||
    purpose === 'padel court balance' ||
    purpose === 'pedal court' ||
    purpose === 'padel court';
  return isPadelPurpose && succeededStatuses.includes(status);
};

const isCancelledInvoice = (invoice: PropertyInvoiceWithRelations) => {
  return (invoice?.status || '').toString().toLowerCase() === 'cancelled';
};

const normalizeStatementStatus = (
  kind: 'debit' | 'credit',
  rawStatus: string,
  outstandingAmount: number
) => {
  if (kind === 'credit') {
    return 'Paid';
  }

  const normalized = (rawStatus || '').toLowerCase();
  if (normalized === 'paid' || outstandingAmount <= 0) {
    return 'Paid';
  }

  return 'Unpaid';
};

const capitalizeFirstLetter = (value?: string) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const PadelCourtStatement: React.FC<OwnProps> = ({ propertyId, invoices = [] }) => {
  const showCards = useMediaQuery(theme.breakpoints.down('md'));
  const [monthFilter, setMonthFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [outstandingOnly, setOutstandingOnly] = useState(false);

  const { data: payments = [] } = useGetPaymentsUserQuery(
    { propertyId },
    { skip: !propertyId }
  );
  const { data: property } = useGetPropertyByIdQuery(
    { id: propertyId },
    { skip: !propertyId }
  );

  const statementRows = useMemo(() => {
    const invoiceRows: StatementRow[] = invoices.map((invoice) => {
      const amount = Number(invoice?.invoice?.totalCost || 0);
      const outstandingAmount = Number(invoice?.propertyInvoiceBalance ?? 0);
      const dueDateRaw = invoice?.invoice?.dueDate || invoice?.dueDate;
      const dueDate = dueDateRaw ? new Date(dueDateRaw) : null;
      const dateLabel = dueDate
        ? dueDate.toLocaleDateString('en-GB', { timeZone: 'UTC' })
        : 'No date';
      const status = (invoice?.status || 'posted').toString();
      const cancelled = isCancelledInvoice(invoice);
      const displayStatus = normalizeStatementStatus('debit', status, outstandingAmount);

      return {
        id: `invoice-${invoice.id || invoice.invoiceNumber}`,
        date: dueDate,
        dateLabel,
        description:
          invoice?.invoice?.invoiceDescription === 'pedalCourtInvoiceBalance'
            ? 'Padel Court Invoice Balance'
            : invoice?.invoice?.invoiceDescription || 'Padel Court Invoice',
        reference: invoice.invoiceNumber || 'Invoice',
        debit: amount,
        credit: 0,
        kind: 'debit',
        status: cancelled ? 'Cancelled' : displayStatus,
        outstandingAmount,
        excludeFromCalculations: cancelled,
      };
    });

    const paymentRows: StatementRow[] = payments
      .filter(isSucceededPadelPayment)
      .map((payment, index) => {
        const amount = Number(payment.amount || 0) / 100;
        const paymentDate = payment.paymentDate ? new Date(payment.paymentDate) : null;
        const dateLabel = paymentDate ? paymentDate.toLocaleDateString('en-GB') : 'No date';

        return {
          id: `payment-${
            payment.id ||
            payment.paymentIntentId ||
            `${payment.paymentDate || 'nodate'}-${payment.amount || 0}-${index}`
          }`,
          date: paymentDate,
          dateLabel,
          description: 'Padel Court Payment',
          reference: payment.paymentIntentId || payment.id || 'Payment',
          debit: 0,
          credit: amount,
          kind: 'credit',
          status: normalizeStatementStatus('credit', payment.status || 'succeeded', 0),
          outstandingAmount: 0,
          excludeFromCalculations: false,
        };
      });

    return [...invoiceRows, ...paymentRows].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date.getTime() - b.date.getTime();
    });
  }, [invoices, payments]);

  const monthOptions = useMemo(() => {
    const map = new Map<string, string>();
    statementRows.forEach((row) => {
      if (!row.date) return;
      const key = `${row.date.getFullYear()}-${String(row.date.getMonth() + 1).padStart(2, '0')}`;
      if (!map.has(key)) {
        map.set(key, row.date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }));
      }
    });

    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([value, label]) => ({ value, label }));
  }, [statementRows]);

  const statusOptions = useMemo(() => {
    const statuses = Array.from(new Set(statementRows.map((row) => row.status.toLowerCase())));
    return statuses.sort();
  }, [statementRows]);

  const filteredRows = useMemo(() => {
    return statementRows.filter((row) => {
      const monthMatch =
        monthFilter === 'all'
          ? true
          : row.date
          ? `${row.date.getFullYear()}-${String(row.date.getMonth() + 1).padStart(2, '0')}` ===
            monthFilter
          : false;

      const statusMatch =
        statusFilter === 'all' ? true : row.status.toLowerCase() === statusFilter;

      const outstandingMatch = outstandingOnly
        ? row.kind === 'debit' && !row.excludeFromCalculations && row.outstandingAmount > 0
        : true;

      return monthMatch && statusMatch && outstandingMatch;
    });
  }, [monthFilter, outstandingOnly, statementRows, statusFilter]);

  const summary = useMemo(() => {
    const totalDebit = filteredRows.reduce(
      (sum, row) => sum + (row.excludeFromCalculations ? 0 : row.debit),
      0
    );
    const totalCredit = filteredRows.reduce(
      (sum, row) => sum + (row.excludeFromCalculations ? 0 : row.credit),
      0
    );
    return {
      totalDebit,
      totalCredit,
      balance: totalDebit - totalCredit,
    };
  }, [filteredRows]);

  const currentPadelBalance = (property as any)?.pedalCourtInvoiceBalance ?? 0;

  const handlePrint = () => {
    window.print();
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        mb: 4,
      }}
    >
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: '1.75rem',
              fontWeight: 600,
              color: '#1976d2',
              borderBottom: '2px solid #e0e0e0',
              paddingBottom: 1,
            }}
          >
            Padel Court Statement
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              '@media print': {
                display: 'none',
              },
            }}
          >
            Print Statement
          </Button>
        </Stack>

        {/* Filter Section */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{
            '@media print': {
              display: 'none',
            },
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="padel-month-filter-label">Month</InputLabel>
            <Select
              labelId="padel-month-filter-label"
              value={monthFilter}
              label="Month"
              onChange={(e: SelectChangeEvent) => setMonthFilter(e.target.value)}
            >
              <MenuItem value="all">All Months</MenuItem>
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="padel-status-filter-label">Status</InputLabel>
            <Select
              labelId="padel-status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {capitalizeFirstLetter(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={outstandingOnly}
                onChange={(e) => setOutstandingOnly(e.target.checked)}
                color="primary"
              />
            }
            label="Outstanding Only"
          />
        </Stack>

        {/* Summary Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8fafc' }}>
            <Typography variant="body2" color="text.secondary">
              Total Invoiced (Debit)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#d32f2f' }}>
              {formatCurrency(summary.totalDebit)}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8fafc' }}>
            <Typography variant="body2" color="text.secondary">
              Total Payments (Credit)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              {formatCurrency(summary.totalCredit)}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8fafc' }}>
            <Typography variant="body2" color="text.secondary">
              Statement Balance
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: summary.balance > 0 ? '#d32f2f' : '#2e7d32',
              }}
            >
              {formatCurrency(Math.abs(summary.balance))}
              <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                {summary.balance > 0 ? '(Owed)' : '(Credit)'}
              </Typography>
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8fafc' }}>
            <Typography variant="body2" color="text.secondary">
              Current Property Balance
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: currentPadelBalance < 0 ? '#d32f2f' : '#2e7d32',
              }}
            >
              {formatCurrency(Math.abs(currentPadelBalance))}
              <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                {currentPadelBalance < 0 ? '(Debit)' : '(Credit)'}
              </Typography>
            </Typography>
          </Paper>
        </Box>

        {/* Statement Rows Table / Mobile Cards */}
        {filteredRows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1">
              No statement records found for the selected criteria.
            </Typography>
          </Box>
        ) : showCards ? (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {filteredRows.map((row) => (
              <ResponsiveRecordCard
                key={row.id}
                eyebrow={row.kind === 'debit' ? 'Invoice' : 'Payment'}
                title={row.reference}
                badge={
                  <Chip
                    label={row.status}
                    color={row.status.toLowerCase() === 'paid' ? 'success' : 'error'}
                    size="small"
                  />
                }
                fields={[
                  {
                    label: 'Date',
                    value: <Typography variant="body2">{row.dateLabel}</Typography>,
                  },
                  {
                    label: 'Description',
                    value: <Typography variant="body2">{row.description}</Typography>,
                  },
                  {
                    label: 'Debit',
                    value: (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: row.debit > 0 ? 700 : 400, color: '#d32f2f' }}
                      >
                        {row.debit > 0 ? formatCurrency(row.debit) : '-'}
                      </Typography>
                    ),
                  },
                  {
                    label: 'Credit',
                    value: (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: row.credit > 0 ? 700 : 400, color: '#2e7d32' }}
                      >
                        {row.credit > 0 ? formatCurrency(row.credit) : '-'}
                      </Typography>
                    ),
                  },
                ]}
              />
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Debit ($)</TableCell>
                  <TableCell align="right">Credit ($)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.dateLabel}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.reference}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={row.status.toLowerCase() === 'paid' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: row.debit > 0 ? 700 : 400,
                        color: row.debit > 0 ? '#d32f2f' : 'inherit',
                      }}
                    >
                      {row.debit > 0 ? formatCurrency(row.debit) : '-'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: row.credit > 0 ? 700 : 400,
                        color: row.credit > 0 ? '#2e7d32' : 'inherit',
                      }}
                    >
                      {row.credit > 0 ? formatCurrency(row.credit) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Paper>
  );
};

export default PadelCourtStatement;
